import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
// import { Camera } from "lucide-react";
import { useAddMCPServer } from '@/modules/gpt-chats/hooks/use-tools';

import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { IAPIError } from '@/modules/gpt-chats/types/tools.service.type';
import { Switch } from '@/components/ui-kit/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui-kit/tooltip';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AddMcpServerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface AddMCPServerFormData {
  toolName: string;
  description: string;
  isOpenTool: boolean;
  isBlocksAuthenticated: boolean;
}

export const AddMcpServerModal = ({ open, onOpenChange }: AddMcpServerModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    control,
    watch,
  } = useForm<AddMCPServerFormData>({
    mode: 'onChange',
    defaultValues: {
      toolName: '',
      description: '',
      isOpenTool: false,
      isBlocksAuthenticated: false,
    },
  });

  const isBlocksToolChecked = watch('isOpenTool');

  const isAdmin = false;
  const { mutateAsync: addMCPServer, isPending } = useAddMCPServer();

  const onSubmit = async (data: AddMCPServerFormData) => {
    try {
      const response = await addMCPServer({
        name: data.toolName.trim(),
        description: data.description?.trim() || '',
        is_open_tool: isAdmin ? data.isOpenTool : false,
        is_blocks_authenticated: isAdmin ? data.isBlocksAuthenticated : false,
      });

      if (response.is_success) {
        showSuccessToast({ description: response.detail || t('MCP_SERVER_ADDED_SUCCESSFULLY') });
        queryClient.invalidateQueries({ queryKey: ['tools'] });
        handleCancel();
      } else {
        showErrorToast({ errors: response.message || t('FAILED_TO_ADD_MCP_SERVER') });
      }
    } catch (error: unknown) {
      const apiError = error as IAPIError;
      if (apiError?.detail && Array.isArray(apiError.detail)) {
        const errorMessages = apiError.detail.map((err) => err.msg).join(', ');
        showErrorToast({ errors: errorMessages });
      } else {
        showErrorToast({ errors: ErrorTransformer(error) });
      }
    }
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">{t('ADD_MCP_SERVER')}</DialogTitle>
          <p className="mt-2 text-sm text-muted-foreground">{t('TOOL_ASSIST_DESCRIPTION')}</p>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          <div className="flex gap-6">
            <div className="flex-1 space-y-4">
              <div className="space-y-2">
                <label htmlFor="toolName" className="text-sm font-medium">
                  {t('TOOL_NAME_LABEL')} <span className="text-destructive">*</span>
                </label>
                <Input
                  id="toolName"
                  placeholder={t('ENTER_NAME')}
                  {...register('toolName', {
                    required: t('TOOL_NAME_REQUIRED'),
                    setValueAs: (v) => v.trim(),
                  })}
                  className={errors.toolName ? 'border-destructive' : ''}
                />
                {errors.toolName && (
                  <p className="text-sm text-destructive">{errors.toolName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium">
                  {t('DESCRIPTION')} <span className="text-destructive">*</span>
                </label>
                <Textarea
                  id="description"
                  placeholder={t('TOOL_DESCRIPTION_PLACEHOLDER')}
                  rows={4}
                  {...register('description', {
                    required: t('DESCRIPTION_IS_REQUIRED'),
                    setValueAs: (v) => v.trim(),
                  })}
                  className={errors.description ? 'border-destructive' : ''}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <Controller
                    name="isOpenTool"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        id="isOpenTool"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    )}
                  />
                  <label htmlFor="isOpenTool" className="cursor-pointer text-sm font-medium">
                    {t('BLOCKS_TOOL_LABEL')}
                  </label>
                </div>
              )}

              {isAdmin && isBlocksToolChecked && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 pt-2">
                    <Controller
                      name="isBlocksAuthenticated"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          id="isBlocksAuthenticated"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      )}
                    />
                    <label
                      htmlFor="isBlocksAuthenticated"
                      className="cursor-pointer text-sm font-medium"
                    >
                      {t('ALLOW_TOOL_AUTHENTICATION')}
                    </label>
                    <Tooltip>
                      <TooltipTrigger type="button" className="cursor-help">
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('ALLOW_AUTH_TOOLTIP')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Switch
                      id="status"
                      checked={false}
                      disabled={true}
                      className="cursor-not-allowed"
                    />
                    <label htmlFor="status" className="text-sm font-medium">
                      {t('TOOL_VISIBILITY')}
                    </label>
                    <Tooltip>
                      <TooltipTrigger type="button" className="cursor-help">
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('ADMIN_ENABLE_TOOL_TOOLTIP')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={handleCancel} disabled={isPending}>
            {t('CANCEL')}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isPending || !isValid}>
            {isPending ? t('SAVING') : t('SAVE')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
